import {compact, groupBy, mapObject} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import {LegacyMeasurement} from 'constants/MeasurementConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {
  AccessibleGridCell,
  Container,
  ContainerContent,
  Grid,
  GridCell,
  GridCellOrEmptyGridCell,
  GridType,
  isAccessibleGridCell,
  isInaccessibleStorable,
  MinimalContainer,
} from 'constants/SampleConstants';
import {MinimalExpirationInfo, MinimalFreezeThawInfo} from 'constants/SampleStatusConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {isSchemaTag, SchemaTag} from 'folder-items/FileConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {BioentitySummary} from 'graphql-client/declarative-translations/external/RESTTypes';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import StorableChip from 'inventory/chips/StorableChip';
import {areUnitsCompatible} from 'inventory/InventoryUnitUtil';
import preferences from 'Preferences';
import {
  ContainerValidContentsInfoById,
  useContainerValidContentsQuery,
} from 'samples/containers/ValidContentsUtils';
import RoleDisplay from 'samples/experimental-roles/ExperimentalRoleDisplay';
import getQuantityDisplayString from 'samples/getQuantityDisplayString';
import {BioentitiesQuery, BioentitiesQueryVariables} from 'samples/grids/__generated/BioentitiesQuery';
import {
  CoordinatesOrderingKey,
  GridCellTableTableRow,
  GridSortOptionsPreferences,
  SortKey,
} from 'samples/grids/GridCellTableConstants';
import {compareFunction} from 'samples/grids/GridCellTableSort';
import GridPosition from 'samples/grids/GridPosition';
import {BIOENTITIES_QUERY} from 'samples/grids/GridQueryUtil';
import GridSortOptions, {
  CoordinatesOrderingItem,
  GridSortConfig,
  GridSortItem,
  HandleGridSortByCoordinates,
} from 'samples/grids/GridSortOptions';
import * as SampleUtil from 'samples/SampleUtil';
import * as StorableContextMenu from 'samples/sidepanel/StorableContextMenu';
import * as TransferUtil from 'samples/transfers/TransferUtil';
import ExpirationDateCell from 'samples/valid-contents/ExpirationDateCell';
import FreezeThawCountCell from 'samples/valid-contents/FreezeThawCountCell';
import * as ExpandedTableTagUtil from 'schemas/tags/ExpandedTableTagUtil';
import {getLinksKey} from 'schemas/tags/TagUtil';
import {SchemaTagForTableDisplay} from 'schemas/tags/TagValue';
import {ChipSize} from 'ui-library/chips/Chip';
import ChipList, {BaseChipList, getChipsRowHeight} from 'ui-library/chips/ChipList';
import * as ChipUtil from 'ui-library/chips/ChipUtil';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {
  ASCENDING_SORT,
  RowSelectionMode,
  ScrollableTableColumn,
  ScrollableTableRow,
  SortDirection,
  TABLE_KEYS,
  TABLE_ROW_BODY_HEIGHT,
} from 'ui-library/scrollable/ScrollableTableConstants';
import SortableScrollableTable, {Sort} from 'ui-library/scrollable/SortableScrollableTable';
import {typeToName} from 'util/NameUtil';
import {ElementMouseEvent} from 'util/Types';

import useContainerMeasurements, {ContainerMeasurementsByContainerId} from './hooks/useContainerMeasurements';

const MAX_VISIBLE_CONTAINED_ITEMS = 3;

const CHIP_SIZE = ChipSize.SMALL;

const MAX_ENTITY_SCHEMAS_WITH_VISIBLE_FIELDS = 3;

const MERGE_COLUMNS_BY_SCHEMA_FIELD_NAME = false;

export const FILLER_TAG_VALUE = '\u2014';

export interface Props {
  cells: Array<GridCell>;
  grid: Grid;
  gridType: GridType;
  onSelectCells: (selectedCells: Array<AccessibleGridCell | null>) => void;
  selectedCells: Array<AccessibleGridCell | null>;
}

interface GridCellTableProps extends Props {
  containedEntityFieldData: {[containerId: string]: Array<BioentitySummary>} | null;
  validContentsInfoByContainerId?: ContainerValidContentsInfoById;
  measurementsByContainerId?: ContainerMeasurementsByContainerId;
}

interface State {
  newlySelectedCell: AccessibleGridCell | null;
}

interface SchemaTagForTableDisplayWithItemIndex {
  itemIndex: number;
  tag: SchemaTagForTableDisplay;
}

const GRID_TABLE_KEY = TABLE_KEYS.GRID_CELL_TABLE;
const DEFAULT_POSITION_ORDER_KEY = CoordinatesOrderingKey.ROW;
const DEFAULT_SORT_KEY = SortKey.COORDINATES;
const DEFAULT_SORT_DIRECTION = SortDirection.ASCENDING_SORT;

export const NOT_SPECIFIED: string = 'Not specified';

interface WellContentConcentrationsCellProps {
  well: Container;
  maxChips?: number;
  chipSize: ChipSize;
  viewAllOverflowLink: JSX.Element;
}

export class WellContentConcentrationsCell extends React.Component<WellContentConcentrationsCellProps> {
  private extractCellConcentrations(container: Container): Array<JSX.Element> {
    const containerContents: Array<ContainerContent> = container.contents;
    if (!containerContents || containerContents.length < 1) {
      return [
        <WellContentConcentrationItem
          text={NOT_SPECIFIED}
          id={container.id}
          itemId={container.id}
          faded={true}
        />,
      ];
    }

    return containerContents.map((containerContent: ContainerContent, idx: number) => {
      const concentration: LegacyMeasurement = containerContent.concentration;
      const concentrationPresent = concentration.value == null || concentration.units == null;
      const text: string = concentrationPresent
        ? NOT_SPECIFIED
        : `${concentration.value} ${concentration.units}`;
      return (
        <WellContentConcentrationItem
          text={text}
          itemId={containerContent.containedItemId}
          id={`${containerContent.containedItemId}_${idx}`}
          faded={concentrationPresent}
        />
      );
    });
  }

  override render(): JSX.Element {
    return (
      <BaseChipList
        chips={this.extractCellConcentrations(this.props.well)}
        maxChips={this.props.maxChips}
        chipSize={this.props.chipSize}
        viewAllOverflowLink={this.props.viewAllOverflowLink}
      />
    );
  }
}

interface WellContentConcentrationItemProps {
  faded: boolean;
  text: string;
  id: string;
  itemId: string;
}

export function WellContentConcentrationItem(props: WellContentConcentrationItemProps): JSX.Element {
  return (
    <div key={props.id} className="chip--mini">
      <span className={classNames({'u-faded': props.faded})}>{props.text}</span>
    </div>
  );
}

function isCellSelected(
  selectedCells: Array<AccessibleGridCell | null>,
  cell: GridCellOrEmptyGridCell
): boolean {
  const nonNullCells = selectedCells.filter(
    (cell): cell is AccessibleGridCell => cell != null && isAccessibleGridCell(cell)
  );
  return nonNullCells.some(
    ({rowIndex, columnIndex}) => rowIndex === cell.rowIndex && columnIndex === cell.columnIndex
  );
}

/**
 * For the given grid, gets a list of entity summaries with schema field information for all
 * contents in each container that are accessible and visible within the max shown items.
 */
function useEntityFieldsByContainer(grid: Grid): {
  [containerId: string]: Array<BioentitySummary>;
} | null {
  const accessibleCells = grid.cells.flat().filter((c): c is AccessibleGridCell => isAccessibleGridCell(c));
  const bioentityIds: Set<string> = new Set();
  for (const cell of accessibleCells) {
    for (const content of cell.container.contents) {
      bioentityIds.add(content.containedItemId);
    }
  }
  const queryResult = useSkippableBenchlingQuery<BioentitiesQuery, BioentitiesQueryVariables>(
    BIOENTITIES_QUERY,
    {
      skip: !BenchlingConfig.INVENTORY_ENABLE_GRID_VIEW_ENTITY_SCHEMA_FIELDS,
      variables: {ids: Array.from(bioentityIds)},
    }
  );
  if (queryResult.type !== QueryResultType.SUCCESS) {
    return null;
  }
  const bioentitiesById = Object.fromEntries(
    accessibleObjs(queryResult.data.bioentities).map((entity) => [
      entity.id,
      graphQLToREST('BioentitySummary', entity),
    ])
  );
  return accessibleCells.reduce((entityFields: {[containerId: string]: Array<BioentitySummary>}, cell) => {
    entityFields[cell.container.id] = cell.container.contents
      .slice(0, MAX_VISIBLE_CONTAINED_ITEMS)
      .map((content) => bioentitiesById[content.containedItemId])
      .filter((content) => content != null);
    return entityFields;
  }, {});
}

export default function QueryingGridCellTable(props: Props): JSX.Element {
  const containerIds = props.cells
    .filter(({container}) => !isInaccessibleStorable(container))
    .map((cell) => (cell.container as MinimalContainer).id);
  const entityFieldsByContainer = useEntityFieldsByContainer(props.grid);
  const containers = props.cells.flatMap(
    ({container}): Array<Container> => (!isInaccessibleStorable(container) ? [container] : [])
  );
  const validContentsInfo = useContainerValidContentsQuery(containers);
  const containerMeasurements = useContainerMeasurements(containerIds);

  return (
    <GridCellTable
      {...props}
      containedEntityFieldData={entityFieldsByContainer}
      validContentsInfoByContainerId={validContentsInfo?.validContentsInfoByContainerId}
      measurementsByContainerId={containerMeasurements.containersData}
    />
  );
}

class GridCellTable extends React.Component<GridCellTableProps, State> {
  override state: State = {
    newlySelectedCell: null,
  };

  override UNSAFE_componentWillReceiveProps(nextProps: GridCellTableProps): void {
    const selectionChanged = !isEqual(this.props.selectedCells, nextProps.selectedCells);
    // Inaccessible cells can't be selected
    const accessibleCells = nextProps.cells.filter((cell): cell is AccessibleGridCell =>
      isAccessibleGridCell(cell)
    );
    if (selectionChanged) {
      this.setState({
        newlySelectedCell:
          accessibleCells.find((cell) => {
            // Is the cell newly selected
            const prevSelected = isCellSelected(this.props.selectedCells, cell);
            const nextSelected = isCellSelected(nextProps.selectedCells, cell);
            return !prevSelected && nextSelected;
          }) || null,
      });
    }
  }

  private handleRowClick = (row: GridCellTableTableRow): void => {
    const {cell} = row.extra;
    // If the cell is EmptyGridCell or inaccessible, do nothing.
    if (!isAccessibleGridCell(cell)) {
      return;
    }
    // If the cell is already selected, deselect it.
    if (isCellSelected(this.props.selectedCells, cell)) {
      // eslint-disable-next-line no-restricted-syntax
      this.props.onSelectCells(_.without(this.props.selectedCells, cell));
      return;
    }
    this.props.onSelectCells(this.props.selectedCells.concat(cell));
  };

  private handleRowsSelect = (rows: Array<GridCellTableTableRow>, isSelected: boolean): void => {
    const cells = rows
      .map((row) => row.extra.cell)
      .filter((cell): cell is AccessibleGridCell => isAccessibleGridCell(cell));
    let newSelectedCells;
    if (isSelected) {
      // eslint-disable-next-line no-restricted-syntax
      newSelectedCells = _.uniq(this.props.selectedCells.concat(cells));
    } else {
      newSelectedCells = this.props.selectedCells.filter((cell) => cell == null || !cells.includes(cell));
    }
    this.props.onSelectCells(newSelectedCells);
  };

  private handleOpenContextMenu = (row: GridCellTableTableRow, e: ElementMouseEvent): void => {
    // we do not want to allow right clicks on empty or inaccessible rows to open the context menu
    if (isAccessibleGridCell(row.extra.cell)) {
      StorableContextMenu.open(e.nativeEvent, row.extra.cell.container);
    }
  };

  private handleOpenContainer = (e: ElementMouseEvent, container: {id: string}): void => {
    e.stopPropagation();
    // Without this _.defer, we run into a React Invariant violation error when opening a container.
    _.defer(() => {
      FileBrowserActions.openFile(container.id);
    });
  };

  private getContainedItemTagColumns(): Array<ScrollableTableColumn> {
    const entities = Object.values(this.props.containedEntityFieldData ?? {}).flat();
    const tagColumns = ExpandedTableTagUtil.listFileTagColumns(entities, {
      includeCustomTagColumns: false,
      mergeBySchemaFieldName: MERGE_COLUMNS_BY_SCHEMA_FIELD_NAME,
    });

    const schemas = entities
      .map((entity) => entity.tagSchema)
      .filter((schema): schema is TagSchema => schema != null);
    const schemaCounts = _.countBy(schemas, 'id');
    // Get list of schema names to map to field names
    const schemaNamesForVisibleFields = _.chain(schemas)
      .uniq('id')
      .sortBy((schema) => schemaCounts[schema.id])
      .reverse() // Most-to-least frequent schema
      .slice(0, MAX_ENTITY_SCHEMAS_WITH_VISIBLE_FIELDS) // Limit to MAX_ENTITY_SCHEMAS_WITH_VISIBLE_FIELDS number of schemas
      .map((schema) => schema.fields.map(() => schema.name)) // Repeat name for each field
      .flatten()
      .value();

    // Limit fields to those for top MAX_ENTITY_SCHEMAS_WITH_VISIBLE_FIELDS schemas
    const visibleSchemaFieldColumns = tagColumns.slice(0, schemaNamesForVisibleFields.length);
    return visibleSchemaFieldColumns.map((column, i) => ({
      ...column,
      header: `${column.header} (${schemaNamesForVisibleFields[i]})`,
      sortKey: `contents_${
        column.sortKey
      }:fromSchemaFieldId:${ExpandedTableTagUtil.getSchemaFieldIdFromColumnKey(column.columnKey)}`,
    }));
  }

  private getCollapsedChipContainedItemFieldValues(containerId: string): Array<SchemaTag> {
    // For entities in a given container, group tags by tag schema field id
    // Merge link targets for accurate row height calculation
    const tags = (
      this.props.containedEntityFieldData?.[containerId].flatMap((item) => item.tags) ?? []
    ).flatMap((tag) => (isSchemaTag(tag) ? [tag] : []));
    const groupedTags = groupBy(tags, ({tagSchemaField}) => tagSchemaField.id);
    return Object.values(
      mapObject(groupedTags, (elem) =>
        elem.every((tag) => ChipUtil.tagShouldRenderChips(tag))
          ? [
              elem.reduce((joinedTag, tag) => {
                const linksKey = getLinksKey(joinedTag.tagSchemaField.fieldType);
                return {
                  ...joinedTag,
                  [linksKey]: [
                    ...(joinedTag[linksKey] as Array<unknown>),
                    ...(tag[linksKey] as Array<unknown>),
                  ],
                };
              }),
            ]
          : elem
      )
    ).flat();
  }

  private getCellsIncludingEmptyCells(): Array<GridCellOrEmptyGridCell> {
    return this.props.grid.cells.flatMap((row, rowIndex) =>
      row.map((cell, columnIndex) => cell ?? {columnIndex, rowIndex, container: null})
    );
  }

  private getRowContentHeight = (tableRowIndex: number): number => {
    const cells = this.getCellsIncludingEmptyCells();
    if (tableRowIndex >= cells.length) {
      return TABLE_ROW_BODY_HEIGHT;
    }
    const container = cells[tableRowIndex].container;
    if (container == null || isInaccessibleStorable(container)) {
      return TABLE_ROW_BODY_HEIGHT;
    }
    const maxNumContentsLinks = container.contents.length ?? 1;
    const containedItemTagsCollapsedFieldValues = this.getCollapsedChipContainedItemFieldValues(container.id);
    // Return max height after considering: contents chips, container field chips, & contained item field chips
    // Increment MAX_VISIBLE_CONTAINED_ITEMS by 1 because we need space for "View all (X)" chip to render in the row
    return Math.max(
      getChipsRowHeight(Math.min(maxNumContentsLinks, MAX_VISIBLE_CONTAINED_ITEMS + 1), CHIP_SIZE),
      ExpandedTableTagUtil.getRowHeight(container.tags, MAX_VISIBLE_CONTAINED_ITEMS),
      ExpandedTableTagUtil.getRowHeight(
        containedItemTagsCollapsedFieldValues,
        maxNumContentsLinks === 1 ? MAX_VISIBLE_CONTAINED_ITEMS : maxNumContentsLinks
      )
    );
  };

  private getContainerTagColumns(): Array<ScrollableTableColumn> {
    const containers = this.props.cells.flatMap(({container}) =>
      !isInaccessibleStorable(container) ? [container] : []
    );
    return ExpandedTableTagUtil.listStorableTagColumns(containers);
  }

  private getContainerTagsByColumn(
    containerTagColumns: Array<ScrollableTableColumn>,
    container: Container
  ): {[tagColumnName: string]: JSX.Element} {
    return ExpandedTableTagUtil.mapTagsToColumns({
      tags: container.tags,
      tagColumns: containerTagColumns,
      maxChipsPerRow: MAX_VISIBLE_CONTAINED_ITEMS,
    }).tagFields;
  }

  private getOrderedRealAndFillerItemTagsForColumns(
    containedItems: Array<BioentitySummary>,
    itemTagListsByTagSchema: {
      // Per tag schema, an array of tags for each entity of that schema
      [tagSchemaId: string]: Array<Array<SchemaTagForTableDisplayWithItemIndex>>;
    }
  ): Array<SchemaTagForTableDisplay> {
    // For each tag schema, collect all tags by tag schema field
    // Within each group, order by index of the item the tag belongs to
    // Fill in gaps (for columns that don't belong to the given entity's schema) with filler tags (value = "--")
    // Returns a flat list of tags ordered by schema, then field, then entity; ex:
    // [Schema1_Field1_Value1, Schema1_Field1_Value2, Schema1_Field2_Value1, Schema1_Field2_Value2, Schema2_Field1_Value1,...]
    return Object.values(itemTagListsByTagSchema).reduce(
      (allTags: Array<SchemaTagForTableDisplay>, perContainedItemTagLists) => {
        if (perContainedItemTagLists.length === 0) {
          return allTags;
        }
        // zip to collect same tag fields together
        const orderedTagsForSchema: Array<SchemaTagForTableDisplay> = _.zip(...perContainedItemTagLists)
          .map((tagsForField: Array<SchemaTagForTableDisplayWithItemIndex>) => {
            // For each schema field, collect and order tags by index of the item they belong to
            // eslint-disable-next-line benchlint/no-spread-reduce
            const realTagsByItemIndex: {[index: number]: SchemaTagForTableDisplay} = tagsForField.reduce(
              (
                realTagsByItemIndexCollector: {[index: number]: SchemaTagForTableDisplay},
                {itemIndex, tag}
              ) => ({
                ...realTagsByItemIndexCollector,
                [itemIndex]: tag,
              }),
              {}
            );
            const fillerTag: SchemaTagForTableDisplay = {
              name: tagsForField[0].tag.name,
              tagSchemaField: tagsForField[0].tag.tagSchemaField,
              value: FILLER_TAG_VALUE,
              isFiller: true,
            };

            // Construct ordered list of tags, with fillers added as needed
            return containedItems.map(
              (item, index) => realTagsByItemIndex[index] ?? {...fillerTag, taggedItemName: item.name}
            );
          })
          .flat();
        return [...allTags, ...orderedTagsForSchema];
      },
      []
    );
  }

  private getAllContainedItemTags(containedItems: Array<BioentitySummary>): Array<SchemaTagForTableDisplay> {
    const itemTagListsByTagSchema = containedItems.reduce(
      (
        itemTagListsByTagSchemaCollector: {
          [tagSchemaId: string]: Array<Array<SchemaTagForTableDisplayWithItemIndex>>;
        },
        item,
        itemIndex
      ) => {
        const itemTagsWithIndex = item.tags
          .filter((tag): tag is SchemaTag => isSchemaTag(tag))
          .map((tag) => ({
            tag: {...tag, taggedItemName: item.name},
            itemIndex,
          }));
        return {
          ...itemTagListsByTagSchemaCollector,
          [item.tagSchema!.id]: [
            ...(itemTagListsByTagSchemaCollector[item.tagSchema!.id] ?? []),
            itemTagsWithIndex,
          ],
        };
      },
      {}
    );

    return this.getOrderedRealAndFillerItemTagsForColumns(containedItems, itemTagListsByTagSchema);
  }

  private getContainedItemTagsByColumn(
    containedItemTagColumns: Array<ScrollableTableColumn>,
    container: Container
  ): {[tagColumnName: string]: JSX.Element} | null {
    const containedItems = this.props.containedEntityFieldData?.[container.id];
    let itemTags: Array<SchemaTagForTableDisplay> = [];
    if (containedItems !== undefined) {
      if (containedItems.length <= 1) {
        itemTags = containedItems
          .map((containedItem) =>
            containedItem.tags
              .filter((tag): tag is SchemaTag => isSchemaTag(tag))
              .map((tag) => ({...tag, taggedItemName: containedItem.name}))
          )
          .flat();
      } else {
        itemTags = this.getAllContainedItemTags(containedItems);
      }
    }

    const contentsFieldOverflowLink = (numLinks: number): JSX.Element => (
      <div className="chip--mini" key="overflow_link">
        <a
          onClick={(e: ElementMouseEvent) => {
            this.handleOpenContainer(e, container);
          }}
        >
          View all ({numLinks})
        </a>
      </div>
    );

    return ExpandedTableTagUtil.mapTagsToColumns({
      tags: itemTags,
      tagColumns: containedItemTagColumns,
      multipleTagsPerColumn: true,
      mergeBySchemaFieldName: MERGE_COLUMNS_BY_SCHEMA_FIELD_NAME,
      maxChipsPerRow: MAX_VISIBLE_CONTAINED_ITEMS,
      collapsedMultiLinkOverflowElement: contentsFieldOverflowLink,
    }).tagFields;
  }

  private getMergedContainedItemTags(containerId: string): Array<SchemaTag> {
    // For entities in a given container, group tags by tag schema field id
    // Merge all non-empty/non-filler values for that tag into one string for sorting
    // i.e. {name: 'tag1', value: 'val1'}, {name: 'tag1', value: '--'}, {name: 'tag1', value: 'val3'} =>
    // {name: 'tag1', value: 'val1\nval3'}
    const containedTags = _.groupBy(
      this.getAllContainedItemTags(this.props.containedEntityFieldData![containerId]),
      ({tagSchemaField}) => tagSchemaField.id
    );
    return Object.values(
      mapObject(containedTags, (elem) =>
        elem.reduce((joinedTag, tag) => {
          // Strip -- and only include real tag values (for sorting)
          const value =
            joinedTag.value === FILLER_TAG_VALUE
              ? tag.value
              : tag.value === FILLER_TAG_VALUE
                ? joinedTag.value
                : [joinedTag.value, tag.value].join('\n');
          return {...joinedTag, value: value?.trim()};
        })
      )
    );
  }

  private cellToIndex(selectedCell: AccessibleGridCell | null): number | null {
    const cells = this.getCellsIncludingEmptyCells();
    return selectedCell
      ? cells.findIndex(
          (cell) => cell.rowIndex === selectedCell.rowIndex && cell.columnIndex === selectedCell.columnIndex
        )
      : null;
  }

  private handleGridSortChangeForCoordinates(
    currentSortList: Array<Sort>,
    onSortListChange: (updatedSortList: Array<Sort>) => void
  ): HandleGridSortByCoordinates {
    return ({
      tableKey,
      sortKey,
      newCoordinatesOrderingKey,
      newSortDirection,
    }: {
      tableKey: string;
      sortKey: string;
      newCoordinatesOrderingKey?: string;
      newSortDirection?: SortDirection;
    }) => {
      const sortIdx = currentSortList.findIndex((sort) => sort.sortKey === sortKey);
      // Sort direction defaults to ascending sort
      let sortDirection = ASCENDING_SORT;
      let customOrderingBehaviorKey;
      if (sortIdx > -1) {
        ({customOrderingBehaviorKey, sortDirection} = currentSortList[sortIdx]);
      }
      // Enforce that coordinates is the primary sort
      const newSortList = currentSortList.filter((sort, i) => i !== sortIdx);
      newSortList.unshift({
        sortKey,
        sortDirection: newSortDirection ?? sortDirection,
        customOrderingBehaviorKey: newCoordinatesOrderingKey ?? customOrderingBehaviorKey,
      });

      if (newCoordinatesOrderingKey && newCoordinatesOrderingKey !== customOrderingBehaviorKey) {
        preferences.set(
          `${tableKey}.${GridSortOptionsPreferences.COORDINATES_ORDER_BY_KEY}`,
          newCoordinatesOrderingKey
        );
      }
      if (newSortDirection && newSortDirection !== sortDirection) {
        preferences.set(
          `${tableKey}.${GridSortOptionsPreferences.COORDINATES_SORT_DIRECTION}`,
          newSortDirection
        );
      }
      onSortListChange(newSortList);
    };
  }

  private shouldShowWellRoleColumn(): Boolean {
    const hasAssignedRoles = this.props.grid.cells.some((row) =>
      row.some((cell) => isAccessibleGridCell(cell) && cell.container.role != null)
    );
    return SampleUtil.hasNewPlateAnnotationFillFeatures(this.props.grid) && hasAssignedRoles;
  }

  private getWellRoleColumn(): ScrollableTableColumn {
    return {
      header: 'Well role',
      accessorKey: 'wellRole',
      flexGrow: 1,
      isCollapsible: false,
      isSortable: false,
    };
  }

  private renderWellRole(container: Container): JSX.Element | null {
    return container.role ? <RoleDisplay role={container.role} /> : null;
  }

  override render(): JSX.Element {
    const containedItemTagColumns = this.getContainedItemTagColumns();

    const containerTagColumns = this.getContainerTagColumns();

    const availableSortOptions: Array<GridSortItem> = [
      {
        sortKey: SortKey.COORDINATES,
        reverse: false,
        sortKeyDisplayName: 'Sort ascending',
        sortIcon: 'arrow-up',
      },
      {
        sortKey: SortKey.COORDINATES,
        reverse: true,
        sortKeyDisplayName: 'Sort descending',
        sortIcon: 'arrow-down',
      },
    ];

    const availableCoordinateOrderOptions: Array<CoordinatesOrderingItem> = [
      {
        sortKey: SortKey.COORDINATES,
        orderKey: CoordinatesOrderingKey.ROW,
        orderKeyDisplayName: 'Order by rows',
        orderIcon: 'fill-across-rows',
      },
      {
        sortKey: SortKey.COORDINATES,
        orderKey: CoordinatesOrderingKey.COLUMN,
        orderKeyDisplayName: 'Order by columns',
        orderIcon: 'fill-down-columns',
      },
    ];
    const gridSortConfig: GridSortConfig = {
      tableKey: GRID_TABLE_KEY,
      availableSortOptions,
      availableCoordinateOrderOptions,
    };

    const columns = compact<ScrollableTableColumn>([
      {
        header: 'Position',
        accessorKey: 'coordinates',
        sortKey: SortKey.COORDINATES,
        isCollapsible: false,
        isSortable: true,
        width: 120, // Wide enough to display both the position and the coordinates
        isFixed: true,
      },
      this.shouldShowWellRoleColumn() ? this.getWellRoleColumn() : null,
      !SampleUtil.isFixedPlate(this.props.grid)
        ? {
            header: 'Container',
            accessorKey: 'container',
            flexGrow: 1,
            isCollapsible: false,
            isSortable: false,
          }
        : null,
      {
        header: getQuantityDisplayString(),
        accessorKey: 'volume',
        sortKey: SortKey.VOLUME,
        isCollapsible: false,
        isSortable:
          this.props.measurementsByContainerId &&
          areUnitsCompatible(
            Object.values(this.props.measurementsByContainerId)
              .map((c) => c.quantity.unit)
              .filter((u) => u != null)
          ),
      },
      {
        header: 'Contents',
        accessorKey: 'contents',
        flexGrow: 1,
        isCollapsible: false,
        isSortable: false,
      },
      {
        header: 'Concentration',
        accessorKey: 'concentration',
        flexGrow: 0.5,
        isCollapsible: true,
        isSortable: false,
      },
      ...(BenchlingConfig.INVENTORY_ENABLE_EXPIRATION_DATE
        ? [
            {
              header: 'Expiration date',
              accessorKey: 'expirationDate',
              flexGrow: 0.5,
              isCollapsible: true,
              isSortable: false,
            },
          ]
        : []),
      ...(BenchlingConfig.INVENTORY_ENABLE_FREEZE_THAW
        ? [
            {
              header: 'Freeze/thaw count',
              accessorKey: 'freezeThawCount',
              flexGrow: 0.5,
              isCollapsible: true,
              isSortable: false,
            },
          ]
        : []),
    ])
      .concat(containerTagColumns)
      .concat(containedItemTagColumns);

    const rowData = this.getCellsIncludingEmptyCells();
    const rows: Array<ScrollableTableRow> = rowData.map((cell) => {
      const {rowIndex, columnIndex, container} = cell;
      const coordinates = SampleUtil.formatGridPosition({
        gridType: this.props.gridType,
        rowIndex,
        columnIndex,
        width: this.props.grid.cells[0].length,
      });
      const gridPosition = (
        <GridPosition
          rowIndex={rowIndex}
          columnIndex={columnIndex}
          gridType={this.props.gridType}
          gridWidth={this.props.grid.cells[0].length}
        />
      );
      // For empty grid cells without a container, all columns other than the container column and the position
      // column should be blank. The icon should be an empty container icon since the position is empty.
      if (container == null) {
        const data = {
          ...Object.fromEntries(columns.map((column) => [column, null])),
          container: <span className="u-faded">{NOT_SPECIFIED}</span>,
          coordinates: gridPosition,
        };
        return {
          id: coordinates,
          data,
          icon: <BenchlingIcon className="scrollableTable-cell-icon" icon="vial" />,
          extra: {cell},
          isSelected: false,
          isSelectorPresent: false,
          rowOnlyClassName: 'cursorDisabled',
        };
      } else if (isInaccessibleStorable(container)) {
        const data = {
          ...Object.fromEntries(columns.map((column) => [column, null])),
          container: (
            <div>
              <StorableChip id={container.invalidId} size={CHIP_SIZE} />
            </div>
          ),
          coordinates: gridPosition,
        };
        return {
          id: coordinates,
          data,
          icon: <BenchlingIcon className="scrollableTable-cell-icon" icon="vial" />,
          extra: {cell},
          isSelected: false,
          isSelectorPresent: false,
          rowOnlyClassName: 'cursorDisabled',
        };
      }
      const expirationInfo: MinimalExpirationInfo | undefined =
        this.props.validContentsInfoByContainerId?.[container.id]?.expirationInfo;
      const freezeThawInfo: MinimalFreezeThawInfo | undefined =
        this.props.validContentsInfoByContainerId?.[container.id]?.freezeThawInfo;
      const data = {
        container: (
          <div>
            <StorableChip id={container.id} size={CHIP_SIZE} onClick={this.handleOpenContainer} />
          </div>
        ),

        wellRole: this.renderWellRole(container),

        coordinates: gridPosition,

        volume: (
          <span className={classNames({'u-faded': container.quantity.value == null})}>
            {TransferUtil.getLegacyMeasurementString(container.quantity)}
          </span>
        ),

        contents: (
          <ChipList
            itemIds={container.contents.map((content) => content.containedItemId)}
            chipSize={CHIP_SIZE}
            maxChips={MAX_VISIBLE_CONTAINED_ITEMS}
            viewAllOverflowLink={
              <div className="chip--mini" key="overflow_link">
                <a
                  onClick={(e: ElementMouseEvent) => {
                    this.handleOpenContainer(e, container);
                  }}
                >
                  View all ({container.contents.length})
                </a>
              </div>
            }
          />
        ),

        concentration: (
          <WellContentConcentrationsCell
            well={container}
            maxChips={MAX_VISIBLE_CONTAINED_ITEMS}
            chipSize={CHIP_SIZE}
            viewAllOverflowLink={
              <div className="chip--mini" key="overflow_link">
                <a
                  onClick={(e: ElementMouseEvent) => {
                    this.handleOpenContainer(e, container);
                  }}
                >
                  View all ({container.contents.length})
                </a>
              </div>
            }
          />
        ),
        expirationDate: <ExpirationDateCell expirationInfo={expirationInfo} />,
        freezeThawCount: <FreezeThawCountCell freezeThawInfo={freezeThawInfo} />,
        ...this.getContainerTagsByColumn(containerTagColumns, container),
        ...this.getContainedItemTagsByColumn(containedItemTagColumns, container),
      };
      const extra = {
        cell,
        containedTags: this.props.containedEntityFieldData
          ? this.getMergedContainedItemTags(container.id)
          : undefined,
        containerQuantity: this.props.measurementsByContainerId?.[container.id]?.quantity,
      };
      return {
        id: coordinates,
        data,
        icon: <BenchlingIcon className="scrollableTable-cell-icon" icon={container.storableSchema.iconId} />,
        extra,
        isSelected: isCellSelected(this.props.selectedCells, cell),
      };
    });

    return (
      <SortableScrollableTable
        className={css(styles.table)}
        rowSelectionMode={RowSelectionMode.MULTIPLE}
        canResizeColumns={true}
        tableKey={GRID_TABLE_KEY}
        columns={columns}
        compareFunction={compareFunction}
        emptyTableText={`This ${typeToName(this.props.gridType)} is empty.`}
        getRowContentHeight={this.getRowContentHeight}
        initialRows={rows}
        initialSort={[
          {
            sortKey: DEFAULT_SORT_KEY,
            sortDirection:
              preferences.get(`${GRID_TABLE_KEY}.${GridSortOptionsPreferences.COORDINATES_SORT_DIRECTION}`) ??
              DEFAULT_SORT_DIRECTION,
            customOrderingBehaviorKey:
              preferences.get(`${GRID_TABLE_KEY}.${GridSortOptionsPreferences.COORDINATES_ORDER_BY_KEY}`) ??
              DEFAULT_POSITION_ORDER_KEY,
          },
        ]}
        getCreateCustomSortElementByColumn={(sortList, onSortListChange) => ({
          // eslint-disable-next-line react/no-unstable-nested-components -- Disabling for now, but worth fixing
          coordinates: ({column, sortIconClassNames}) => (
            <GridSortOptions
              onGridSort={this.handleGridSortChangeForCoordinates(sortList, onSortListChange)}
              gridSortConfig={gridSortConfig}
              iconClassNames={sortIconClassNames}
              column={column}
            />
          ),
        })}
        isCompact={true}
        matchParentHeight={true}
        onRowClick={this.handleRowClick}
        onRowContextMenu={this.handleOpenContextMenu}
        onRowsSelect={this.handleRowsSelect}
        scrollToRow={this.cellToIndex(this.state.newlySelectedCell) ?? undefined}
      />
    );
  }
}

const styles = StyleSheet.create({table: {height: '100%'}});
